import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import apiService from "../../services/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  Grid,
  Paper,
  Divider,
  Chip,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const EmailTemplateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [template, setTemplate] = useState(null);
  const [mounted, setMounted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    description: "",
    body_html: "",
    body_text: "",
    variables: [],
    is_active: true,
  });
  
  const [htmlCode, setHtmlCode] = useState(""); // Raw HTML code

  // Set mounted state
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Load template if editing or copying
  useEffect(() => {
    if (isEditing) {
      loadTemplate();
    } else {
      // Check if we're copying from query params
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("copy") === "true") {
        setFormData({
          name: urlParams.get("name") || "",
          subject: urlParams.get("subject") || "",
          description: urlParams.get("description") || "",
          body_html: urlParams.get("body_html") || "",
          body_text: urlParams.get("body_text") || "",
          variables: urlParams.get("variables")
            ? JSON.parse(urlParams.get("variables"))
            : [],
          is_active: urlParams.get("is_active") === "true",
        });
      }
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await apiService.get(`/email/templates/${id}`);

      if (data.success) {
        setTemplate(data.data);
        const bodyHtml = data.data.body_html || data.data.body || "";
        setFormData({
          name: data.data.name,
          subject: data.data.subject,
          description: data.data.description,
          body_html: bodyHtml,
          body_text: data.data.body_text || "",
          variables: data.data.variables ? JSON.parse(data.data.variables) : [],
          is_active: data.data.is_active,
        });
        setHtmlCode(bodyHtml);
      } else {
        setAlert({ severity: "error", message: "Failed to load template" });
      }
    } catch (error) {
      console.error("Failed to load template:", error);
      setAlert({ severity: "error", message: "Failed to load template" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const extractVariables = (body) => {
    if (!body || typeof body !== "string") {
      return [];
    }

    try {
      // Extract exact variable names including the {var_...} format
      const matches = body.match(/\{var_\w+\}/g);
      if (matches) {
        // Return unique exact variable names
        return [...new Set(matches)];
      }
    } catch (error) {
      console.warn("Failed to extract variables:", error);
    }
    return [];
  };

  const handleBodyChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
        variables:
          field === "body_html" ? extractVariables(value) : prev.variables,
      };

      // Sync HTML code when RTE content changes
      if (field === "body_html") {
        setHtmlCode(value);
      }

      return newData;
    });
  };
  
  // Handle HTML code changes (when pasting HTML directly)
  const handleHtmlCodeChange = (value) => {
    setHtmlCode(value);
    handleBodyChange("body_html", value);
  };
  
  // Sync HTML code when formData.body_html changes externally (but not from htmlCode changes)
  useEffect(() => {
    if (formData.body_html && formData.body_html !== htmlCode) {
      setHtmlCode(formData.body_html);
    }
  }, [formData.body_html]);

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      setAlert({ severity: "error", message: "Template name is required" });
      return;
    }
    if (!formData.subject.trim()) {
      setAlert({ severity: "error", message: "Subject is required" });
      return;
    }
    if (!formData.body_html.trim()) {
      setAlert({ severity: "error", message: "HTML body is required" });
      return;
    }

    try {
      setSaving(true);

      let data;
      if (isEditing) {
        data = await apiService.put(`/email/templates/${id}`, formData);
      } else {
        data = await apiService.post("/email/templates", formData);
      }

      if (data.success) {
        setAlert({
          severity: "success",
          message: isEditing
            ? "Template updated successfully"
            : "Template created successfully",
        });

        // Redirect back to template list after a short delay
        setTimeout(() => {
          navigate("/superadmin/email-templates");
        }, 1500);
      } else {
        setAlert({
          severity: "error",
          message: data.message || "Failed to save template",
        });
      }
    } catch (error) {
      console.error("Failed to save template:", error);
      setAlert({ severity: "error", message: "Failed to save template" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/superadmin/email-templates");
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">Loading template...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate("/superadmin/email-templates")}
          sx={{ cursor: "pointer" }}
        >
          Email Templates
        </Link>
        <Typography color="text.primary">
          {isEditing ? "Edit Template" : "Create Template"}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            {isEditing ? "Edit Email Template" : "Create New Email Template"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isEditing
              ? `Editing: ${template?.name}`
              : "Create a new email template with dynamic variables"}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
        >
          Back to Templates
        </Button>
      </Box>

      {alert && (
        <Alert
          severity={alert.severity}
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

        {/* Template Form */}
        <Card>
          <CardContent>
            {/* Basic Information */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Basic Information
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Enter template name"
                required
                helperText="Unique name for this template"
              />
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={(e) => handleInputChange("subject", e.target.value)}
                placeholder="Enter email subject"
                required
                helperText="Email subject line"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                multiline
                rows={2}
                placeholder="Brief description of when to use this template"
                helperText="Brief description of when to use this template"
              />
            </Box>

            {/* Email Content */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Email Content
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                HTML Body
              </Typography>

              <Grid container spacing={2}>
                {/* Rich Text Editor - Left Side (50%) */}
                <Grid item xs={12} md={6} sx={{ width: '48%' }}>
                  <Box sx={{ border: "1px solid #ddd", borderRadius: "4px" }}>
                    <Typography variant="caption" sx={{ p: 1, display: 'block', fontWeight: 'bold', borderBottom: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
                      Rich Text Editor
                    </Typography>
                    <Editor
                      apiKey="ftcbeoyg6l9bq6ufxeti5sqzr41charmed0645gipdsy9ymu"
                      value={formData.body_html}
                      onEditorChange={(content) =>
                        handleBodyChange("body_html", content)
                      }
                      init={{
                        height: 400,
                        menubar: false,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "image",
                          "charmap",
                          "preview",
                          "anchor",
                          "searchreplace",
                          "visualblocks",
                          "code",
                          "fullscreen",
                          "insertdatetime",
                          "media",
                          "table",
                          "code",
                          "help",
                          "wordcount",
                        ],
                        toolbar:
                          "undo redo | formatselect | " +
                          "bold italic backcolor | alignleft aligncenter " +
                          "alignright alignjustify | bullist numlist outdent indent | " +
                          "removeformat | link image | help",
                        content_style:
                          "body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; }",
                        placeholder:
                          "Start typing your email content here... Use {var_variable_name} for dynamic parameters.",
                        branding: false,
                        elementpath: false,
                        statusbar: false,
                        resize: false,
                      }}
                    />
                  </Box>
                </Grid>

                {/* HTML Code - Right Side (48%) */}
                <Grid item xs={12} md={6} sx={{ width: '48%' }}>
                  <Box sx={{ border: "1px solid #ddd", borderRadius: "4px" }}>
                    <Typography variant="caption" sx={{ p: 1, display: 'block', fontWeight: 'bold', borderBottom: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
                      HTML Code
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={16}
                      value={htmlCode}
                      onChange={(e) => handleHtmlCodeChange(e.target.value)}
                      placeholder="Paste or type HTML code here..."
                      sx={{
                        '& .MuiInputBase-root': {
                          fontFamily: 'monospace',
                          fontSize: '13px',
                        },
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                        },
                        '& fieldset': {
                          border: 'none',
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Use {"{var_variable_name}"} for dynamic parameters. Variables
                are automatically detected. Changes in either view will sync automatically.
              </Typography>
            </Box>

            {/* Template Settings */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Template Settings
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Detected Variables
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  minHeight: 40,
                  alignItems: "center",
                }}
              >
                {formData.variables.length > 0 ? (
                  formData.variables.map((variable, index) => (
                    <Chip
                      key={index}
                      label={variable}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No variables detected. Use {"{var_name}"} format in HTML
                    body.
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Variables are shown in exact format: {"{var_variable_name}"}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      handleInputChange("is_active", e.target.checked)
                    }
                  />
                }
                label="Template Active"
              />
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Inactive templates won't appear in dropdowns
              </Typography>
            </Box>
          </CardContent>
        </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? "Saving..."
            : isEditing
            ? "Update Template"
            : "Create Template"}
        </Button>
      </Box>
    </Box>
  );
};

export default EmailTemplateForm;
